// Layout/Layout.jsx
import React, { useState, useEffect } from "react";
import { Menu, Search, Bell, User } from "lucide-react";
import Sidebar from "./Sidebar";
import NotificationPanel from "./NotificationPanel";

export default function Layout({ children, userData }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, text: "Nueva comisión recibida", time: "Hace 10m", read: false },
    { id: 2, text: "Meta mensual alcanzada", time: "Hace 1h", read: false },
    { id: 3, text: "Actualización del sistema", time: "Hace 3h", read: true },
  ]);
  
  // Manejar clic fuera del sidebar en dispositivos móviles
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.getElementById("sidebar");
      const menuButton = document.getElementById("menu-button");

      if (
        sidebar &&
        sidebarOpen &&
        !sidebar.contains(event.target) &&
        menuButton &&
        !menuButton.contains(event.target) &&
        window.innerWidth < 768
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map((notif) => ({ ...notif, read: true })));
  };

  // Calcular notificaciones no leídas
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="flex flex-col min-h-screen bg-gray-950 text-gray-100">
      {/* Top navigation - Siempre visible */}
      <header className="bg-gray-900 p-3 sm:p-4 shadow-md flex justify-between items-center fixed top-0 left-0 right-0 z-30">
        <div className="flex items-center gap-3">
          <button
            id="menu-button"
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Menu"
          >
            <Menu size={24} />
          </button>
          <h1 className="text-lg sm:text-xl font-bold">AfiliaApp</h1>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <div className="relative hidden sm:block">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Buscar..."
              className="bg-gray-800 py-1 pl-9 pr-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-40 lg:w-64"
            />
          </div>

          <div className="relative">
            <button
              onClick={toggleNotifications}
              className="relative p-2 hover:bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Notificaciones"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Panel de notificaciones */}
            {showNotifications && (
              <NotificationPanel 
                notifications={notifications} 
                markAllAsRead={markAllAsRead} 
              />
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
              {userData.avatar ? (
                <img
                  src={userData.avatar}
                  alt="Avatar"
                  className="h-full w-full object-cover"
                />
              ) : (
                <User size={18} />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <Sidebar 
        userData={userData} 
        sidebarOpen={sidebarOpen} 
      />

      {/* Main content */}
      <main className="flex-1 pt-16 md:pl-64">
        {/* Overlay para cerrar sidebar en móvil */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Dashboard content */}
        <div className="p-4 sm:p-6 max-w-full overflow-hidden">
          {children}
        </div>
      </main>
    </div>
  );
}

